import type { ColorInput, ColorTint } from '../helpers/normalizeColor';
import { computed, type MaybeRefOrGetter } from 'vue';
import { useColorResolver } from './useColorResolver';

export function useColor(
    value?: MaybeRefOrGetter<ColorInput | null | undefined>,
    tint?: MaybeRefOrGetter<ColorTint | null | undefined>
) {
    const resolver = useColorResolver();

    const color = computed(() => resolver(value, tint));

    const clone = () => color.value.clone();

    const rgb = computed(() => clone().rgb);
    const valid = computed(() => clone().valid);

    const grayscale = () => clone().greyscale();
    const alpha = (a: number) => clone().alpha(a);
    const clearer = (ratio: number) => clone().clearer(ratio);
    const opaquer = (ratio: number) => clone().opaquer(ratio);
    const negate = () => clone().negate();
    const lighten = (ratio: number) => clone().lighten(ratio);
    const darken = (ratio: number) => clone().darken(ratio);
    const saturate = (ratio: number) => clone().saturate(ratio);
    const desaturate = (ratio: number) => clone().desaturate(ratio);
    const rotate = (deg: number) => clone().rotate(deg);
    const mix = (
        weight: number,
        color: MaybeRefOrGetter<ColorInput>,
        tint?: MaybeRefOrGetter<ColorTint | null | undefined>
    ) => clone().mix(resolver(color, tint), weight);

    const rgbString = computed(() => clone().rgbString());
    const hexString = computed(() => clone().hexString());

    return {
        clone,
        rgb,
        valid,
        grayscale,
        alpha,
        clearer,
        opaquer,
        negate,
        lighten,
        darken,
        saturate,
        desaturate,
        rotate,
        rgbString,
        hexString,
        mix,
    };
}
